import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaCamera, FaArrowLeft, FaUser, FaEnvelope, FaPhone, FaGraduationCap, FaMapMarkerAlt, FaCalendarAlt, FaVenusMars, FaBook, FaExternalLinkAlt } from "react-icons/fa";
import bg from "../../assets/first_bg.png"; // background image
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/apiConfig";
import { toast } from "sonner";
import WOW from "wowjs";
import "animate.css";
import { ClipLoader, RingLoader } from "react-spinners";
import profilePlaceholder from '../../assets/profile.jpg'
import { Shield } from "lucide-react";
import { jwtDecode } from "jwt-decode";

const StudentProfilePage = () => {
    const token = localStorage.getItem("token")
    const user = jwtDecode(token);
    const userId = user?.user_id;
    const [student, setStudent] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(profilePlaceholder);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const navigate = useNavigate();

    const openModal = () => setModalOpen(true);
    const closeModal = () => {
        setModalOpen(false);
        setSelectedImage(null);
    };

    // Parse subjects array safely
    const parseSubjects = (subjectsString) => {
        try {
            return JSON.parse(subjectsString || '[]');
        } catch (error) {
            return [];
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "Not provided";
        try {
            return new Date(dateString).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch (error) {
            return "Not provided";
        }
    };

    // Format date for input field
    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        try {
            return new Date(dateString).toISOString().split('T')[0];
        } catch (error) {
            return "";
        }
    };

    // Fetch user profile
    useEffect(() => {
        const fetchProfile = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/users/me`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch user profile");
                }
                const userData = await response.json();
                console.log(userData)
                const filteredData = {
                    firstName: userData.firstName || "",
                    lastName: userData.lastName || "",
                    email: userData.email || "",
                    phoneNumber: userData.phoneNumber || "",
                    gender: userData.gender || "",
                    dob: userData.dob || "",
                    country: userData.country || "",
                    state: userData.state || "",
                    accountType: userData.accountType || "",
                    yearGroup: userData.yearGroup || "",
                    school: userData.school || "",
                    subjects: userData.subjects || "[]",
                    guardianEmail: userData.guardianEmail || "",
                    isVerified: userData.isVerified || false,
                    subscribed: userData.subscribed || false
                };

                setStudent(filteredData);
                setFormData({
                    ...filteredData,
                    dob: formatDateForInput(userData.dob)
                });

                if (userData.profile_image) {
                    setProfileImagePreview(userData.profile_image);
                } else {
                    setProfileImagePreview(profilePlaceholder);
                }
            } catch (error) {
                console.error("Error fetching user profile:", error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProfile();
    }, [userId]);

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Handle image selection and preview
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            setProfileImagePreview(URL.createObjectURL(file));
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <ClipLoader color="#4A90E2" size={50} />
                <p className="ml-4 text-blue-600 font-medium">Loading your profile...</p>
            </div>
        );
    }

    // Handle profile image update
    const handleImageUpdate = async () => {
        try {
            if (!selectedImage) {
                toast.error("Please select an image to upload.");
                return;
            }

            const formData = new FormData();
            formData.append("profile_image", selectedImage);

            const response = await fetch(`${API_BASE_URL}/api/users/profile-image/${userId}`, {
                method: "PATCH",
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update profile image");
            }

            const result = await response.json();
            toast.success(result.message || "Profile image updated successfully.");

            if (result.profile_image) {
                setProfileImagePreview(result.profile_image);
            }
            closeModal();
        } catch (error) {
            console.error("Error updating profile image:", error.message);
            toast.error("Failed to update profile image.");
        }
    };

    const handleUpdateDetails = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No token found. User might not be logged in.");
            return;
        }
        try {
            // Only send non-academic fields for update
            const updateData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phoneNumber: formData.phoneNumber,
                gender: formData.gender,
                dob: formData.dob,
                country: formData.country,
                state: formData.state,
                guardianEmail: formData.guardianEmail
            };

            const response = await fetch(`${API_BASE_URL}/api/users/details`, {
                method: "POST", // Backend treats this as partial update
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(updateData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update user details");
            }

            const updatedData = await response.json();
            const filteredData = {
                firstName: updatedData.user.firstName || "",
                lastName: updatedData.user.lastName || "",
                email: updatedData.user.email || "",
                phoneNumber: updatedData.user.phoneNumber || "",
                gender: updatedData.user.gender || "",
                dob: updatedData.user.dob || "",
                country: updatedData.user.country || "",
                state: updatedData.user.state || "",
                accountType: updatedData.user.accountType || "",
                yearGroup: updatedData.user.yearGroup || "",
                school: updatedData.user.school || "",
                subjects: updatedData.user.subjects || "[]",
                guardianEmail: updatedData.user.guardianEmail || "",
                isVerified: updatedData.user.isVerified || false,
                subscribed: updatedData.user.subscribed || false
            };

            setStudent(filteredData);
            setFormData({
                ...filteredData,
                dob: formatDateForInput(updatedData.user.dob)
            });
            toast.success("Profile updated successfully!");
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating user details:", error.message);
            toast.error("Failed to update profile. Please try again.");
        }
    };

    // Delete profile
    const handleDelete = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete profile");
            }

            toast.success("Profile deleted successfully!");
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            navigate("/login");
        } catch (error) {
            console.error("Error deleting profile:", error.message);
            toast.error("Failed to delete profile. Please try again.");
        }
    };

    const handleDeleteImage = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/users/profile-image/${userId}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete profile image");
            }

            const result = await response.json();
            toast.success(result.message || "Profile image deleted successfully.");
            setProfileImagePreview(profilePlaceholder);
            closeModal();
        } catch (error) {
            console.error("Error deleting profile image:", error.message);
            toast.error(error.message || "Failed to delete profile image.");
        }
    };

    // Navigate back
    const goBack = () => {
        navigate(-1);
    };

    // Navigate to onboarding for academic details
    const goToOnboarding = () => {
        navigate('/update-academics');
    };

    // Profile field configuration - only editable fields
    const editableProfileFields = [
        { key: 'firstName', label: 'First Name', icon: FaUser, type: 'text' },
        { key: 'lastName', label: 'Last Name', icon: FaUser, type: 'text' },
        { key: 'phoneNumber', label: 'Phone Number', icon: FaPhone, type: 'tel' },
        { key: 'gender', label: 'Gender', icon: FaVenusMars, type: 'select', options: ['Male', 'Female', 'Other'] },
        { key: 'dob', label: 'Date of Birth', icon: FaCalendarAlt, type: 'date' },
        { key: 'country', label: 'Country', icon: FaMapMarkerAlt, type: 'text' },
        { key: 'state', label: 'State/Province', icon: FaMapMarkerAlt, type: 'text' },
        { key: 'guardianEmail', label: 'Guardian Email', icon: Shield, type: 'email' }
    ];

    // All profile fields for display
    const allProfileFields = [
        { key: 'firstName', label: 'First Name', icon: FaUser, type: 'text' },
        { key: 'lastName', label: 'Last Name', icon: FaUser, type: 'text' },
        { key: 'email', label: 'Email Address', icon: FaEnvelope, type: 'email' },
        { key: 'phoneNumber', label: 'Phone Number', icon: FaPhone, type: 'tel' },
        { key: 'gender', label: 'Gender', icon: FaVenusMars, type: 'select' },
        { key: 'dob', label: 'Date of Birth', icon: FaCalendarAlt, type: 'date' }
    ];

    const locationFields = [
        { key: 'country', label: 'Country', icon: FaMapMarkerAlt },
        { key: 'state', label: 'State/Province', icon: FaMapMarkerAlt }
    ];

    const academicFields = [
        { key: 'yearGroup', label: 'Year Group', icon: FaGraduationCap },
        { key: 'school', label: 'School', icon: FaGraduationCap }
    ];
    // console.log(profileImagePreview)

    return (
        <div
            className="min-h-screen flex flex-col justify-center items-center py-8"
            style={{
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}
        >
            <div className="bg-white bg-opacity-95 rounded-2xl shadow-2xl w-[95%] max-w-4xl p-6 md:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <button
                        onClick={goBack}
                        className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition-colors duration-200"
                    >
                        <FaArrowLeft className="text-lg" />
                        Back
                    </button>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${student.isVerified ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <span className="text-sm font-medium">{student.isVerified ? 'Verified' : 'Unverified'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${student.subscribed ? 'bg-blue-500' : 'bg-gray-400'}`}></div>
                            <span className="text-sm font-medium">{student.subscribed ? 'Subscribed' : 'Free Plan'}</span>
                        </div>
                    </div>
                </div>

                {/* Profile Header */}
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
                    <div className="relative group">
                        <img
                            src={profileImagePreview || profilePlaceholder}
                            alt="Profile"
                            className="w-32 h-32 rounded-full shadow-lg object-cover object-center border-4 border-white"
                        />
                        <button
                            onClick={openModal}
                            className="absolute -bottom-2 -right-2 bg-[#2148C0] hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-all duration-200 transform hover:scale-110"
                        >
                            <FaCamera className="text-sm" />
                        </button>
                    </div>

                    <div className="text-center md:text-left">
                        <h1 className="text-3xl font-bold text-[#2148C0] mb-2">
                            {`${student.firstName || 'Not'} ${student.lastName || 'Provided'}`}
                        </h1>
                        <p className="text-gray-600 mb-2 flex items-center justify-center md:justify-start gap-2">
                            <FaEnvelope className="text-sm" />
                            {student.email || 'Not provided'}
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                            <span className="bg-[#2148C0] text-white px-3 py-1 rounded-full text-sm capitalize">
                                {student.accountType || 'Student'}
                            </span>
                            {student.yearGroup && (
                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                                    {student.yearGroup}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Profile Content */}
                <div className="bg-gray-50 rounded-xl p-6">
                    {!isEditing ? (
                        <div className="space-y-6">
                            {/* Personal Information */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Personal Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {allProfileFields.map(({ key, label, icon: Icon }) => (
                                        <div key={key} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                                            <Icon className="text-[#2148C0] text-lg flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">{label}</p>
                                                <p className="text-gray-800">
                                                    {key === 'dob' ? formatDate(student[key]) : (student[key] || 'Not provided')}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Location Information */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Location</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {locationFields.map(({ key, label, icon: Icon }) => (
                                        <div key={key} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                                            <Icon className="text-[#2148C0] text-lg flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">{label}</p>
                                                <p className="text-gray-800">{student[key] || 'Not provided'}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Academic Information */}
                            <div>
                                <div className="flex items-center justify-between mb-4 border-b pb-2">
                                    <h3 className="text-lg font-semibold text-gray-800">Academic Information</h3>
                                    <button
                                        onClick={goToOnboarding}
                                        className="text-sm text-[#2148C0] hover:text-blue-600 font-medium flex items-center gap-1 transition-colors duration-200"
                                    >
                                        Update Academic Info <FaExternalLinkAlt className="text-xs" />
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {academicFields.map(({ key, label, icon: Icon }) => (
                                            <div key={key} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                                                <Icon className="text-[#2148C0] text-lg flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-600">{label}</p>
                                                    <p className="text-gray-800">{student[key] || 'Not provided'}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Subjects */}
                                    <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                                        <FaBook className="text-[#2148C0] text-lg flex-shrink-0 mt-1" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-600 mb-2">Subjects</p>
                                            <div className="flex flex-wrap gap-2">
                                                {parseSubjects(student.subjects).length > 0 ? (
                                                    parseSubjects(student.subjects).map((subject, index) => (
                                                        <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm">
                                                            {subject}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-gray-500 text-sm">No subjects selected</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Guardian Information */}
                                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                                        <Shield className="text-[#2148C0] text-lg flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-600">Guardian Email</p>
                                            <p className="text-gray-800">{student.guardianEmail || 'Not provided'}</p>
                                        </div>
                                    </div>

                                    {/* Info box about academic details */}
                                    <div className="bg-blue-50 border-l-4 border-[#2148C0] p-4 rounded-lg">
                                        <div className="flex items-start gap-3">
                                            <FaGraduationCap className="text-[#2148C0] text-lg flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-800 mb-1">Academic Information Updates</p>
                                                <p className="text-sm text-gray-600">
                                                    To update your year group, school, or subjects, please visit the update academics page where you can make comprehensive changes to your academic profile.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="bg-[#2148C0] text-white px-6 py-3 rounded-lg shadow-md hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center gap-2"
                                >
                                    <FaEdit /> Edit Personal Details
                                </button>
                                <button
                                    onClick={goToOnboarding}
                                    className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-green-700 transition-colors duration-200 flex items-center justify-center gap-2"
                                >
                                    <FaGraduationCap /> Update Academic Info
                                </button>
                                <button
                                    onClick={() => setDeleteModalOpen(true)}
                                    className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-red-700 transition-colors duration-200 flex items-center justify-center gap-2"
                                >
                                    <FaTrash /> Delete Profile
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Edit Form - Only Personal Details */
                        <form
                            className="space-y-6"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleUpdateDetails();
                            }}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-800">Edit Personal Information</h3>
                                <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">
                                    Academic details can be updated on the onboarding page
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {editableProfileFields.map(({ key, label, type, options }) => (
                                    <div key={key} className="flex flex-col">
                                        <label className="font-medium text-gray-700 mb-2">{label}</label>
                                        {type === 'select' ? (
                                            <select
                                                name={key}
                                                value={formData[key] || ""}
                                                onChange={handleInputChange}
                                                className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2148C0] focus:border-transparent"
                                            >
                                                <option value="">Select {label}</option>
                                                {options?.map((option) => (
                                                    <option key={option} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type={type}
                                                name={key}
                                                value={formData[key] || ""}
                                                onChange={handleInputChange}
                                                className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2148C0] focus:border-transparent"
                                                placeholder={`Enter ${label.toLowerCase()}`}
                                            />
                                        )}
                                    </div>
                                ))}

                                {/* Email field - readonly */}
                                <div className="flex flex-col">
                                    <label className="font-medium text-gray-700 mb-2">Email Address</label>
                                    <input
                                        type="email"
                                        value={student.email || ""}
                                        className="p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                                        placeholder="Email cannot be changed"
                                        readOnly
                                        disabled
                                    />
                                    <p className="text-sm text-gray-500 mt-1">Email cannot be modified</p>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
                                <button
                                    type="submit"
                                    className="bg-[#2148C0] text-white px-6 py-3 rounded-lg shadow-md hover:bg-blue-600 transition-colors duration-200"
                                >
                                    Save Changes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditing(false);
                                        // Reset form data to original student data
                                        setFormData({
                                            ...student,
                                            dob: formatDateForInput(student.dob)
                                        });
                                    }}
                                    className="bg-gray-500 text-white px-6 py-3 rounded-lg shadow-md hover:bg-gray-600 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* Image Upload Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
                        <h2 className="text-2xl font-semibold text-center mb-6 text-gray-800">Update Profile Picture</h2>

                        <div className="flex flex-col items-center space-y-4">
                            <div className="relative">
                                <img
                                    src={profileImagePreview || profilePlaceholder}
                                    alt="Preview"
                                    className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 shadow-lg"
                                />
                            </div>

                            <label className="flex items-center justify-center bg-[#2148C0] text-white px-6 py-3 rounded-lg shadow-md hover:bg-blue-600 cursor-pointer transition-colors duration-200">
                                <FaCamera className="mr-2" />
                                Choose New Image
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                            </label>

                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={handleImageUpdate}
                                    className="flex-1 bg-green-600 text-white py-3 rounded-lg shadow-md hover:bg-green-700 transition-colors duration-200"
                                    disabled={!selectedImage}
                                >
                                    Upload
                                </button>

                                {profileImagePreview !== profilePlaceholder && (
                                    <button
                                        onClick={handleDeleteImage}
                                        className="flex-1 bg-red-600 text-white py-3 rounded-lg shadow-md hover:bg-red-700 transition-colors duration-200"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>

                            <button
                                onClick={closeModal}
                                className="w-full bg-gray-300 text-gray-800 py-3 rounded-lg shadow-md hover:bg-gray-400 transition-colors duration-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                        <div className="text-center">
                            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <FaTrash className="text-red-600 text-2xl" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Delete Profile</h3>
                            <p className="text-gray-600 mb-6">
                                Are you sure you want to permanently delete your profile? This action cannot be undone.
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={handleDelete}
                                    className="flex-1 bg-red-600 text-white py-3 rounded-lg shadow-md hover:bg-red-700 transition-colors duration-200"
                                >
                                    Delete
                                </button>
                                <button
                                    onClick={() => setDeleteModalOpen(false)}
                                    className="flex-1 bg-gray-300 text-gray-800 py-3 rounded-lg shadow-md hover:bg-gray-400 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentProfilePage;